<template>
    <div style="position: relative">
        <GoogleMap
            ref="map"
            :api-promise="apiPromise.type"
            :center="center"
            :zoom="getZoom()"
            :max-zoom="maxZoom ?? 18"
            :disable-default-ui="true"
            :styles="mapOptions.styles!"
            :style="{
                width: '100%',
                height: height,
                'border-radius': borderRadius,
                overflow: 'hidden',
            }">
            <Marker
                :key="index"
                v-for="(marker, index) in markers ?? []"
                :options="marker"
                @click="openInfoWindow(index)">
                <InfoWindow style="color: var(--ion-background-color)" v-model="infoWindowOpenToggles[index]">
                    <h6>{{ marker.title }}</h6>
                    <div class="marker-info-window-content" v-html="marker.infoWindowHTML"></div>
                </InfoWindow>
            </Marker>
        </GoogleMap>
        <div
            v-if="disableInteractions"
            class="map-overlay"
            :class="{ visible: isPressing }"
            @mousedown="startPress"
            @mouseup="cancelPress"
            @mouseleave="cancelPress"
            @touchstart="startPress"
            @touchend="cancelPress"
            @touchcancel="cancelPress"></div>
        <div v-if="isPressing" class="map-overlay-message">
            <span class="map-overlay-message-text">Premi e tieni premuto per sbloccare la mappa...</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, PropType, watch, computed, reactive, nextTick } from "vue";
import { IonButton, IonIcon, modalController } from "@ionic/vue";
import { lockClosed } from "ionicons/icons";
import { GoogleMap, Marker, InfoWindow } from "vue3-google-map";
import { Loader } from "@googlemaps/js-api-loader";
import { IExtendedMapOptions, IExtendedMapTypeStyle, IExtendedMarkerOptions } from "@/types/maps";
import { debounce } from "@/utility";
import MapModal from "@/components/modals/MapModal.vue";

const loader = new Loader({
    apiKey: "AIzaSyAPB3Rt_DKWfrJSWN3RVunkrU5M43DuXyU",
});
const apiPromise = loader.importLibrary("maps") as any;

const markerClick = (event: any, marker: IExtendedMarkerOptions) => {
    console.log(marker);
};

// PROPS
const props = defineProps({
    fixZoom: {
        type: Number,
        required: false,
    },
    maxZoom: {
        type: Number,
        required: false,
    },
    height: {
        type: String,
        required: false,
        default: "300px",
    },
    borderRadius: {
        type: String,
        required: false,
        default: "8px",
    },
    center: {
        type: Object as PropType<google.maps.LatLngLiteral>,
        required: false,
    },
    markersData: {
        type: Array as PropType<IExtendedMarkerOptions[]>,
        required: false,
    },
    disableInteractions: {
        type: Boolean,
        required: false,
        default: true,
    },
    onTap: {
        type: Function as PropType<() => void>,
        required: false,
    },
});
const infoWindowOpenToggles = reactive<boolean[]>([]);
const map = ref<InstanceType<typeof GoogleMap> | null>(null);
const mapOptions = ref<IExtendedMapOptions>({
    styles: [
        {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
        },
        {
            featureType: "transit",
            stylers: [{ visibility: "off" }],
        },
    ] as IExtendedMapTypeStyle[],
});
const interactionsLocked = ref(props.disableInteractions);
const isPressing = ref(false);
let pressTimer: number | null = null;
const markers = computed(() =>
    props.markersData?.map((marker) => ({
        ...marker,
        infoWindowHTML: `${marker.infoWindowHTML ?? ""}<a href="https://www.google.com/maps/search/?api=1&query=${
            marker.position?.lat
        },${marker.position?.lng}" target="_blank" rel="noopener noreferrer">Naviga</a>`,
    })),
);

const enableInteractions = () => {
    // interactionsLocked.value = false;
    // isPressing.value = false;
    // interactionsLocked.value = false;
    // isPressing.value = false;
    props.onTap?.();
    modalController
        .create({
            component: MapModal,
            componentProps: {
                center: props.center,
                markersData: props.markersData,
                fixZoom: props.fixZoom,
                maxZoom: props.maxZoom,
                // height: props.height,
                borderRadius: props.borderRadius,
                disableInteractions: false,
            },
        })
        .then((modal) => {
            modal.present();
        });
};

const startPress = () => {
    isPressing.value = true;

    pressTimer = window.setTimeout(enableInteractions, 1300); // long press threshold
};

const cancelPress = () => {
    isPressing.value = false;
    if (pressTimer) {
        clearTimeout(pressTimer);
        isPressing.value = false;
        pressTimer = null;
    }
};

const center = computed(() => {
    if (props.center) {
        return props.center;
    }
    return {
        lat: 43.842648397350274,
        lng: 10.504608228928932,
    } as google.maps.LatLngLiteral;
});
const getZoom = () => {
    if (props.fixZoom) {
        return props.fixZoom!;
    }
    return 20;
};
const getBounds = () => {
    const bounds = new google.maps.LatLngBounds();
    props.markersData!.forEach((marker) => {
        bounds.extend({
            lat: marker.position!.lat,
            lng: marker.position!.lng,
        } as google.maps.LatLngLiteral);
    });
    return bounds;
};
const openInfoWindow = (index: any) => {
    infoWindowOpenToggles.fill(false);
    infoWindowOpenToggles[index] = true;
};

const updateMap = async () => {
    if (props.markersData && props.markersData.length > 0 && map.value?.map && !props.center && !props.fixZoom) {
        await nextTick();
        // Small delay to ensure container is fully rendered and sized
        setTimeout(() => {
            if (map.value?.map) {
                google.maps.event.trigger(map.value.map, "resize");
                map.value.map.fitBounds(getBounds(), 0);
            }
        }, 100);
        infoWindowOpenToggles.length = 0;
        infoWindowOpenToggles.push(...Array<boolean>(props.markersData.length).fill(false));
    }
};

const refresh = () => {
    updateMap();
};

defineExpose({ refresh });

watch(
    () => props.markersData,
    debounce(async () => updateMap(), 500),
    { deep: true },
);
watch(
    () => map.value?.mapTilesLoaded,
    (ready) => updateMap(),
    { immediate: true },
);
</script>

<style lang="css">
.map-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--ion-card-background);
    opacity: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
    cursor: pointer;
    z-index: 1;
}
.map-overlay.visible {
    background-color: var(--ion-card-background);
    opacity: 0.8;
}

.map-overlay-message {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    color: var(--ion-text-color);
}

.map-overlay-message-text {
    font-weight: bold;
    display: block;
}

.map-overlay-lock-button {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
}
.gm-style-iw-chr button {
    height: unset !important;
    width: unset !important;
    margin-top: 5px !important;
}
.gm-style-iw-chr button span {
    background-color: black !important;
    margin: 0 !important;
}

.gm-style-iw-d h6 {
    margin-top: 0 !important;
    margin-bottom: 5px !important;
}
.gm-style-iw-d p {
    margin-top: 5px !important;
    margin-bottom: 5px !important;
}
</style>
